import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronRight } from 'lucide-react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Skeleton } from '@/components/ui/skeleton'
import { FloorPriceList } from './FloorPriceList'
import type {
  FloorPrice,
  FloorPricePublisher,
} from '@/services/lineItems/lineItemDiagnosticsApi'

export interface FloorPricesPanelProps {
  publishers: FloorPricePublisher[]
  isLoading: boolean
  isError?: boolean
}

interface TreeRowProps {
  name: ReactNode
  detail?: ReactNode
  /** How many children sit under the row, e.g. "3 sites". */
  childLabel?: string
  floorPrices: FloorPrice[]
  /** Nested rows; the row expands to show them when there are any. */
  children?: ReactNode
}

/** One publisher, site or ad unit: its name, its floor prices, and what sits under it. */
function TreeRow({ name, detail, childLabel, floorPrices, children }: TreeRowProps) {
  const [open, setOpen] = useState(false)

  const body = (
    <>
      <div className="min-w-0 flex-1 text-left">
        <div className="truncate text-sm font-medium">{name}</div>
        {(detail || childLabel) && (
          <div className="truncate text-xs text-(--ad-text-muted)">
            {[childLabel, detail].filter(Boolean).map((part, i) => (
              <span key={i}>
                {i > 0 && ' · '}
                {part}
              </span>
            ))}
          </div>
        )}
      </div>
      <FloorPriceList floorPrices={floorPrices} />
    </>
  )

  if (!children) {
    return <div className="flex items-start gap-3 py-2 pr-1 pl-7">{body}</div>
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="group flex w-full items-start gap-3 rounded-md py-2 pr-1 hover:bg-(--ad-neutral-soft)">
        <ChevronRight className="mt-0.5 size-4 shrink-0 text-(--ad-text-muted) transition-transform group-data-[state=open]:rotate-90" />
        {body}
      </CollapsibleTrigger>
      <CollapsibleContent className="ml-2 border-l pl-3">{children}</CollapsibleContent>
    </Collapsible>
  )
}

/**
 * Every floor price the line item has to beat, publisher by publisher. A
 * price set lower down overrides the one above it, so each level is listed
 * with its own prices rather than flattened.
 */
export function FloorPricesPanel({ publishers, isLoading, isError }: FloorPricesPanelProps) {
  const { t } = useTranslation()

  return (
        <div className="max-h-[28rem] overflow-y-auto">
          {isLoading && (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          )}
          {!isLoading && isError && (
            <p className="text-sm text-destructive">{t('lineItemDiagnostics.loadFailed')}</p>
          )}
          {!isLoading && !isError && publishers.length === 0 && (
            <p className="text-sm text-(--ad-text-muted)">{t('lineItemDiagnostics.noPublishers')}</p>
          )}
          {!isLoading && !isError && publishers.length > 0 && (
            <>
              <p className="mb-3 text-sm text-(--ad-text-muted)">
                {t('lineItemDiagnostics.floorPricesDescription')}
              </p>
              {publishers.map((pub) => (
                <TreeRow
                  key={pub.id}
                  name={pub.name}
                  childLabel={t('lineItemDiagnostics.siteCount', { count: pub.sites.length })}
                  floorPrices={pub.floorPrices}
                >
                  {pub.sites.length > 0 &&
                    pub.sites.map((site) => (
                      <TreeRow
                        key={site.id}
                        name={site.name}
                        detail={site.siteUrl}
                        childLabel={t('lineItemDiagnostics.adUnitCount', {
                          count: site.adUnits.length,
                        })}
                        floorPrices={site.floorPrices}
                      >
                        {site.adUnits.length > 0 &&
                          site.adUnits.map((unit) => (
                            <TreeRow
                              key={unit.id}
                              name={unit.name}
                              detail={unit.dimensions.map(([w, h]) => `${w}×${h}`).join(', ')}
                              floorPrices={unit.floorPrices}
                            />
                          ))}
                      </TreeRow>
                    ))}
                </TreeRow>
              ))}
            </>
          )}
        </div>
  )
}
